import React from "react";
import MainLayout from "./layouts/sidebar/MainLayout";
import { Routes, Route, Navigate } from "react-router-dom";
import ChatWithAgents from "./components/AIAgents/ChatWithAgent/ChatWithAgents";
import VerifyEmail from "./components/auth/VerifyEmail";
import Login from "./components/auth/Login";
import { AuthProvider } from "./context/AuthContext";
import { BrandThemeProvider } from "./context/BrandThemeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import FacebookCallback from "./features/meta/platforms/facebook/components/FacebookCallback";
import InstagramCallback from "./features/meta/platforms/instagram/components/InstagramCallback";
import NewPasswordRequired from "./features/login/NewPasswordRequired";
import MfaChallenge from "./features/login/MfaChallenge";
import ForgotPassword from "./features/login/ForgotPassword";
import OnboardingGate from "./components/OnboardingGate";
import OnboardingAgentPage from "./features/onboarding-agent/OnboardingAgentPage";
import GMBCallback from "./features/google/platforms/gmb/component/GMBCallback";
import GoogleDriveCallback from "./features/google/platforms/google-drive/components/GoogleDriveCallback";
import Dashboard from "./features/dashboard/components/Dashboard";
import CreateAgentForm from "./components/AIAgents/CreateAgent/CreateAgentForm";
import EditAgentForm from "./components/AIAgents/EditAgent/EditAgentForm";
import CloneAgentForm from "./components/AIAgents/CloneAgent/CloneAgentForm";
import ChatAgentPage from "./features/chat-with-agents/ChatAgentPage";
import CustomerManager from "./components/Customer/CustomerManager";
import SocialPostsPageV2 from "./pages/SocialPostsPageV2";
import MediaLibraryPage from "./pages/MediaLibraryPage";
import AvatarPage from "./pages/AvatarPage";
import UserList from "./features/user-management/components/UserList";
import YouTubeCallback from "./features/google/platforms/youtube/component/YouTubeCallback";
import GSCCallback from "./features/google/platforms/gsc/components/GSCCallback";
import AllUsers from "./features/user-management/components/AllUsers";
import BusinessProfileCompletion from "./features/customer-onboarding/components/BusinessProfileCompletion";
import InstagramDirectCallback from "./features/meta/platforms/instagram/components/instagramDirectCallback";
import LinkedInCallback from "./features/linkedin/components/LinkedInCallback";
import TikTokCallback from "./features/tiktok/components/TikTokCallback";
import WordPressCallback from "./features/wordpress/components/WordPressCallback";
import GHLCallback from "./features/ghl/components/GHLCallback";
import ShopifyCallback from "./features/shopify/components/ShopifyCallback";
import SessionExpiredGate from "./components/auth/SessionExpiredGate";
import { ThemeProvider } from "./context/ThemeManager";
import { ToastProvider } from "./context/ToastContext";
import { RedditFeedProvider } from "./context/RedditFeedContext";
import BillingPage from "./pages/BillingPage";
import CardToCrmPage from "./features/card-to-crm/components/CardToCrmPage";

const App = () => {
  return (
    <AuthProvider>
      <ToastProvider>
        {/* Above the routes on purpose: a Reddit feed build must survive the
            user navigating away from the chat window while it runs. */}
        <RedditFeedProvider>
        <BrandThemeProvider>
          <ThemeProvider>
          <SessionExpiredGate />
          <Routes>
        {/* Public routes */}
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/login" element={<Login />} />
        <Route
          path="/connect/facebook/callback"
          element={<FacebookCallback />}
        />
        <Route
          path="/connect/instagram/callback/*" // Add wildcard to handle hash fragments
          element={<InstagramCallback />}
        />
        <Route
          path="/connect/instagram-direct/callback"
          element={<InstagramDirectCallback />}
        />
        <Route
          path="/connect/linkedin/callback"
          element={<LinkedInCallback />}
        />
        <Route
          path="/connect/linkedin/business/callback"
          element={<LinkedInCallback mode="business" />}
        />
        {/* TikTok (SGL-170). Public for the same reason as LinkedIn: AuthContext is
            not reliably rehydrated on return, and a guard would discard the
            single-use code. The callback recovers the token from tiktok_pending_token. */}
        <Route path="/connect/tiktok/callback" element={<TikTokCallback />} />
        <Route
          path="/connect/wordpress/callback"
          element={<WordPressCallback />}
        />
        {/* GoHighLevel. Public like the other backend-exchange callbacks:
            AuthContext is not reliably rehydrated on return from the provider,
            and a guard here would redirect to /login and discard the
            authorization code. The callback recovers the Cognito token from
            ghl_pending_token.

            Path is "/crm/" not "/ghl/" because HighLevel's marketplace refuses
            to register a redirect URL containing a HighLevel reference. See
            features/ghl/config/ghlEnv.ts — do not rename. */}
        <Route path="/connect/crm/callback" element={<GHLCallback />} />
        <Route path="/new-password" element={<NewPasswordRequired />} />
        <Route path="/verify-mfa" element={<MfaChallenge />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="chat-with-agents-chatkit" element={<ChatAgentPage />} />
        {/* Public Card-to-CRM tool — anonymous, bypasses the auth guard */}
        <Route path="/card-to-crm" element={<CardToCrmPage />} />
        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<OnboardingGate />}>
            {/* Full-screen onboarding — no sidebar intentionally */}
            <Route path="onboarding-agent" element={<OnboardingAgentPage />} />
            <Route element={<MainLayout />}>
              <Route index element={<ChatWithAgents />} />
              <Route path="workspace" element={<Dashboard />} />
              <Route path="agents" element={<Navigate to="/" replace />} />
              <Route
                path="agent-dashboard"
                element={<Navigate to="/" replace />}
              />
              <Route path="onboarding" element={<Navigate to="/onboarding-agent" replace />} />
              <Route
                path="connect"
                element={
                  <Navigate to="/social-posts?tab=connect" replace />
                }
              />
              <Route path="create-agent" element={<CreateAgentForm />} />
              <Route path="edit-agent" element={<EditAgentForm />} />
              <Route path="clone-agent" element={<CloneAgentForm />} />
              <Route path="customer" element={<CustomerManager />} />
              <Route path="social-posts" element={<SocialPostsPageV2 />} />
              <Route
                path="blogs"
                element={<Navigate to="/social-posts?tab=blogs" replace />}
              />
              <Route path="media-library" element={<MediaLibraryPage />} />
              <Route path="avatar" element={<AvatarPage />} />
              <Route path="users" element={<UserList />} />
              <Route path="settings" element={<UserList />} />
              <Route path="billing" element={<BillingPage />} />
              <Route
                path="business-profile/complete/:customerId"
                element={<BusinessProfileCompletion />}
              />
              <Route path="/users/all" element={<AllUsers />} />
            </Route>
          </Route>
          <Route path="/connect/google/callback" element={<GMBCallback />} />
          <Route
            path="/connect/google-drive/callback"
            element={<GoogleDriveCallback />}
          />
          <Route
            path="/connect/youtube/callback"
            element={<YouTubeCallback />}
          />
          {/* Inside the layout on purpose: the callback needs the customer workspace
              context, so it must not be a public route. */}
          <Route path="/connect/gsc/callback" element={<GSCCallback />} />
          {/* Same reason as GSC: the customer comes from the pin set at Connect. */}
          <Route path="/connect/shopify/callback" element={<ShopifyCallback />} />
        </Route>
        {/* Catch-all route - redirect to login for unmatched routes */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
        </ThemeProvider>
      </BrandThemeProvider>
      </RedditFeedProvider>
    </ToastProvider>
  </AuthProvider>
  );
};

export default App;